import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatDrawer } from '@angular/material/sidenav';
import { NavigationEnd, Router } from '@angular/router';
import {
  BehaviorSubject,
  filter,
  finalize,
  Subscription,
  switchMap,
  take,
} from 'rxjs';
import { CoreService } from 'src/app/modules/shared/services/core/core.service';
import { MatIconRegistry } from '@angular/material/icon';
import { PopoverController } from '@ionic/angular';
import { Notificacao } from './notifications/notifications.interface';
import { NotificationsComponent } from './notifications/notifications.component';
import {
  MENU_SEM_ROTA,
  MenuOption,
  SidebarService,
} from 'src/app/modules/shared/services/sidebar/sidebar.service';
import { AuthService } from 'src/app/modules/auth/services/auth.service';
import { UniqueIdService } from 'src/app/modules/shared/services/unique-id/unique-id.service';
import { ToastService } from 'src/app/modules/shared/services/toast/toast.service';

/**
 * Os filhos começam na mesma borda de conteúdo da linha de nível 0 — o `.px-5`
 * (40px) que ela recebe enquanto a gaveta está aberta. A coluna de ícone (15%)
 * do nível 0 não entra na conta: submenu não tem ícone, e reservá-la só roubava
 * largura de rótulos que já vinham cortados por reticências.
 */
const RECUO_LATERAL = 40;
/** Degrau somado a cada nível hierárquico. */
const RECUO_POR_NIVEL = 20;

/** Nó do menu visto pelas rotinas de destaque — só a identificação importa. */
type MenuNodeRef = Pick<MenuOption, 'description' | 'route'> &
  Partial<Omit<MenuOption, 'description' | 'route'>>;

/** Caminho, da raiz até a funcionalidade, que casa com a rota atual. */
interface ActiveMenuPath {
  readonly path: readonly string[];
  readonly score: number;
}

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
})
export class NavbarComponent implements OnInit, AfterViewInit {
  @ViewChild('drawer') drawer!: MatDrawer;
  showFiller = false;
  sectionId: number = 0;
  childrenId: string = '';
  menuOptions: MenuOption[] = [];
  openedMenus = new Set<string>();
  activeLeafId: string | null = null;
  isCollapsed = false;
  id;
  userPath;
  userConf;
  urlSelected: string = '';
  editarAuxiliarRegex: any = /^\/auxiliar\/[0-9a-fA-F\-]{36}\/editar$/;
  editarPermissoesRegex: any = /^\/permissoes\/[0-9a-fA-F\-]{36}\/editar$/;
  addPermissionarioRegex: any =
    /^\/permissao\/[0-9a-fA-F\-]{36}\/permissionario$/;
  editarPermissionarioRegex: any =
    /^\/permissao\/[0-9a-fA-F\-]{36}\/permissionario\/[0-9a-fA-F\-]{36}$/;
  editarUsuarioRegex: any = /^\/usuario\/[0-9a-fA-F\-]{36}\/editar$/;
  editarPerfilUsuarioRegex: any =
    /^\/perfil-usuario\/[0-9a-fA-F\-]{36}\/editar$/;
  editarEntidadeRegex: any = /^\/entidade\/[0-9a-fA-F\-]{36}\/editar$/;
  systemName: string | null;
  isVisible: boolean = true;
  tooltipMessage: string = 'Sistema de Gestão do Transporte Público';
  userName: string = '';
  userProfile: string = '';

  permissaoUrls: string[] = [
    '/permissoes/criar',
    '/permissoes',
    this.editarPermissoesRegex,
    this.addPermissionarioRegex,
    this.editarPermissionarioRegex,
  ];
  auxiliaresUrls: string[] = [
    this.editarAuxiliarRegex,
    '/auxiliares',
    '/auxiliares/criar',
  ];
  urlPatterns: { [key: string]: (string | RegExp)[] } = {
    'Gestão de usuários': [
      '/usuarios/novo',
      '/perfil-usuario',
      '/perfil-usuario/novo',
      this.editarPerfilUsuarioRegex,
      this.editarUsuarioRegex,
    ],
    Entidades: ['/entidades/criar', this.editarEntidadeRegex],
    Contratos: ['/criar-contrato'],
    // O menu vem do banco ainda com url '/linhas'; a rota é '/atendimentos'
    Linhas: ['/atendimentos'],
    Atendimentos: ['/atendimentos'],
    Administrando: [...this.auxiliaresUrls, ...this.permissaoUrls],
    Taxi: [...this.auxiliaresUrls, ...this.permissaoUrls],
    Permissão: [...this.permissaoUrls],
    Auxiliares: [...this.auxiliaresUrls],
  };

  blockedNotificationsUrls: string[] = ['/auth/sign-up', '/auth/login'];
  isUrlBlockedToShowNotifications: boolean = false;

  notifications: Notificacao[] = [];
  quantidadeNaoLidas: number = 0;
  subscriptions: Subscription[] = [];
  pageIndex: number = 0;
  isGettingNotifications: boolean = false;
  lastNotificationPage$ = new BehaviorSubject<boolean>(false);
  isFetchingNotifications$ = new BehaviorSubject<boolean>(false);
  private routerSubscription!: Subscription;
  private dataSub!: Subscription;

  constructor(
    private sidebarService: SidebarService,
    private coreService: CoreService,
    private authService: AuthService,
    private sanitizer: DomSanitizer,
    private uniqueIdService: UniqueIdService,
    private router: Router,
    private matIconRegistry: MatIconRegistry,
    private toastService: ToastService,
    private popoverCtrl: PopoverController,
  ) {
    this.id =
      this.uniqueIdService.generateUniqueIdWithPrefix('user-registration');
    this.userPath = this.getSafeUrl('assets/create-user/svgs/user.svg');
    this.userConf = this.getSafeUrl('assets/create-user/svgs/user_conf.svg');

    this.matIconRegistry.addSvgIcon(
      'mail',
      this.sanitizer.bypassSecurityTrustResourceUrl('assets/navbar/mail.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'bell',
      this.sanitizer.bypassSecurityTrustResourceUrl('assets/navbar/bell.svg'),
    );

    this.systemName = localStorage.getItem('SystemName');
  }
  ngAfterViewInit(): void {
    this.drawer.toggle();
  }

  ngOnInit() {
    // Antes de carregar o menu: o trilho ativo é resolvido assim que as opções
    // chegam, e `userId$` pode emitir de forma síncrona.
    this.urlSelected = this.router.url.split('?')[0];

    this.dataSub = this.authService.userId$
      .pipe(
        filter(
          (userId): userId is string => userId !== null && userId !== undefined,
        ),

        switchMap((userId) => this.coreService.getMenuUsuario(userId)),
      )
      .subscribe({
        next: (user: any) => {
          this.menuOptions = this.sidebarService.getMenuOptions(user);
          this.syncActiveTrail();
          this.userName = user.nome;
          this.userProfile = user.profileUser.nome;
          this.authService.setUserInternalId(user.id);
        },
        error: (err) => {
          console.error(err);
        },
      });

    this.toggleDrawer();

    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const nav = event as NavigationEnd;
        const urlSplit = nav.urlAfterRedirects.split('?');
        this.urlSelected = urlSplit[0];
        this.syncActiveTrail();

        this.isUrlBlockedToShowNotifications =
          this.blockedNotificationsUrls.some((route) =>
            this.urlSelected.includes(route),
          );

        this.getNotifications();
      });
  }

  toggleDrawer(): void {
    this.showFiller = !this.showFiller;
    this.isCollapsed = !this.isCollapsed;
    if (!this.showFiller) {
      this.sectionId = 0;
    }
  }

  openSection(id: number) {
    if (this.sectionId == id) {
      this.sectionId = 0;

      this.toggleDrawerAfterSectionClick();
      return;
    }
    this.sectionId = id;
  }

  selectChildren(id: string) {
    this.childrenId = id;
    this.toggleDrawerAfterSectionClick();
  }

  /**
   * Um nó com filhos expande/recolhe; uma funcionalidade final navega
   * (o `routerLink` do template) e apenas fecha o menu no mobile.
   */
  onNodeClick(option: MenuOption): void {
    if (option.hasChildren) {
      this.toggleMenu(option.id);
      return;
    }

    this.selectChildren(option.id);
  }

  expandIcon(id: string): 'keyboard_arrow_up' | 'keyboard_arrow_down' {
    return this.isMenuOpen(id) ? 'keyboard_arrow_up' : 'keyboard_arrow_down';
  }

  /** Recuo progressivo por nível hierárquico, a partir do primeiro filho. */
  childIndent(level: number): number {
    return RECUO_LATERAL + RECUO_POR_NIVEL * level;
  }

  /** Só a funcionalidade final da rota atual recebe o destaque de item ativo. */
  isLeafActive(option: MenuOption): boolean {
    return this.activeLeafId !== null && this.activeLeafId === option.id;
  }

  isNodeActive(option: MenuOption): boolean {
    return option.hasChildren
      ? this.activeStyle(option)
      : this.isLeafActive(option);
  }

  toggleDrawerAfterSectionClick() {
    if (window.innerWidth < 768) {
      this.toggleDrawer();
    }
  }
  showOptions() {
    if (!this.showFiller) {
      const buttonToggle = document.getElementsByClassName(
        'menu-button',
      ) as HTMLCollection;
      const button = buttonToggle[0] as HTMLElement;
      this.sectionId = 0;
      button?.click();
    }
  }
  getSafeUrl(path: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(path);
  }

  activeStyle(option: MenuNodeRef): boolean {
    return this.isUrlInHierarchy(option, this.urlSelected);
  }

  /**
   * Marca a funcionalidade correspondente à rota atual e abre todos os seus
   * ancestrais, sem recolher nada que o usuário já tenha expandido.
   */
  private syncActiveTrail(): void {
    const match = this.findActivePath(this.menuOptions);

    this.activeLeafId = match ? match.path[match.path.length - 1] : null;
    match?.path
      .slice(0, -1)
      .forEach((ancestorId) => this.openedMenus.add(ancestorId));
  }

  /** Caminho até a funcionalidade final que melhor casa com a rota atual. */
  private findActivePath(
    nodes: MenuOption[],
    trail: readonly string[] = [],
  ): ActiveMenuPath | null {
    let best: ActiveMenuPath | null = null;

    for (const node of nodes) {
      const path = [...trail, node.id];
      const candidate = node.hasChildren
        ? this.findActivePath(node.children, path)
        : this.leafMatch(node, path);

      if (candidate && (!best || candidate.score > best.score)) {
        best = candidate;
      }
    }

    return best;
  }

  private leafMatch(
    node: MenuOption,
    path: readonly string[],
  ): ActiveMenuPath | null {
    const score = this.matchScore(node);

    return score > 0 ? { path, score } : null;
  }

  /**
   * Quanto mais específica a rota do item, maior a pontuação — assim
   * `/permissoes/criar` não destaca também o item `/permissoes`.
   */
  private matchScore(option: MenuOption): number {
    const route = option.route;

    if (this.urlSelected && route && route !== MENU_SEM_ROTA) {
      if (this.urlSelected === route) {
        return route.length + 1;
      }

      if (this.urlSelected.startsWith(`${route}/`)) {
        return route.length;
      }
    }

    return this.isActiveSubsection(option) ? 1 : 0;
  }

  private isUrlInHierarchy(option: MenuNodeRef, targetUrl: string): boolean {
    if (targetUrl === '') {
      return false;
    }

    if (option.route === targetUrl) {
      return true;
    }

    if (targetUrl.startsWith(option.route) && option.route !== '') {
      return true;
    }

    if (this.isActiveSubsection(option)) {
      return true;
    }

    if (option.children && option.children.length > 0) {
      return option.children.some((childOption) =>
        this.isUrlInHierarchy(childOption, targetUrl),
      );
    }

    return false;
  }

  private isActiveSubsection(section: MenuNodeRef): boolean {
    return this.urlPatterns[section.description]?.some((pattern) => {
      if (typeof pattern === 'string') {
        return (
          pattern === this.urlSelected || this.urlSelected.startsWith(pattern)
        );
      } else {
        return pattern.test(this.urlSelected);
      }
    });
  }

  toggleMenu(id: string) {
    if (this.openedMenus.has(id)) {
      this.openedMenus.delete(id);
    } else {
      this.openedMenus.add(id);
    }
  }

  isMenuOpen(id: string): boolean {
    return this.openedMenus.has(id);
  }

  logout() {
    this.authService.logout();
  }

  getNotifications(pageIndex: number = 0) {
    if (this.authService.isAuthenticated() === false) return;
    if (this.isGettingNotifications) return;
    if (this.isUrlBlockedToShowNotifications) return;

    this.isGettingNotifications = true;
    this.isFetchingNotifications$.next(true);

    this.subscriptions.push(
      this.coreService
        .getNotifications(pageIndex)
        .pipe(
          take(1),
          finalize(() => {
            this.isGettingNotifications = false;
            this.isFetchingNotifications$.next(false);
          }),
        )
        .subscribe({
          next: (resp: any) => {
            const { content, pageNumber, quantidadeNaoLidas, last } = resp;
            if (pageIndex === 0) {
              this.notifications = [];
            }

            this.pageIndex = pageNumber;
            this.quantidadeNaoLidas = quantidadeNaoLidas
              ? quantidadeNaoLidas
              : 0;
            this.notifications.push(...content);
            this.lastNotificationPage$.next(last);
          },
          error: (erro) => {
            if (erro.status !== 500 && erro.status) {
              this.toastService.showError(erro.error.message);
            } else {
              this.toastService.showError('Erro ao carregar notificações');
            }
          },
        }),
    );
  }

  async openPopover(ev: any) {
    const popover = await this.popoverCtrl.create({
      component: NotificationsComponent,
      event: ev,
      cssClass: 'notifications-popover',
      animated: true,
      side: 'bottom',
      alignment: 'center',
      size: 'auto',
      showBackdrop: false,
      mode: 'md',
      componentProps: {
        notifications: this.notifications,
        lastPage$: this.lastNotificationPage$,
        isFetching$: this.isFetchingNotifications$,
        viewMoreNotifications: (pageIndex: number) => {
          this.getNotifications(pageIndex);
        },
      },
    });
    await popover.present();

    const { role } = await popover.onDidDismiss();

    if (role === 'backdrop') {
      this.getNotifications();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.routerSubscription?.unsubscribe();
    this.dataSub?.unsubscribe();
  }
}
